import { useCallback } from 'react'
import { Appointment } from '@myapp/contract'
import { GhostState, DefaultDurations, ResizeHandle } from './types'
import { findNearestAvailableSlot } from '../../../shared/lib/findNextAvailableSlot'
import { useDispositionDraftStore } from './useDispositionDraftStore'
import { ResolvedBreak } from '../../../entities/time-entry/model/useBreakBlock'

function getHourFromIso(iso: string): number {
  const d = new Date(iso)
  return d.getUTCHours() + d.getUTCMinutes() / 60
}

function isTechWorkingDay(workingDays: number[], dayIso: string): boolean {
  const dow = new Date(dayIso).getDay()
  const mapped = dow === 0 ? 7 : dow
  return workingDays.includes(mapped)
}

interface UseTimelineInteractionsOptions {
  ghost: GhostState | null
  setGhost: (ghost: GhostState | null) => void
  commitGhost: () => void
  clearGhost: () => void
  draftDurations: DefaultDurations
  setDraftDurations: (d: DefaultDurations) => void
  totalDraftDuration: number
  resizeSnapshotRef: React.MutableRefObject<{ durations: DefaultDurations; anfahrtStartIso: string } | null>
  resizeHandleRef: React.MutableRefObject<ResizeHandle | null>
  editingAppointment?: Appointment
  workOrderId?: string
  appointmentId?: string
  technicians?: { technicianId: string; technicianName: string | null; appointments: Appointment[]; workingDays: number[]; shiftStart: string | null; shiftEnd: string | null }[]
  breakBlocks?: Map<string, ResolvedBreak>
  hasCollision: (iso: string, dur: DefaultDurations, techId: string, excludeId?: string) => boolean
  dayIso?: string
  onDraftFinalized?: (
    technicianId: string,
    technicianName: string | null,
    anfahrtStartIso: string,
    durations: DefaultDurations,
    tradeCode?: number,
  ) => void
}

export function useTimelineInteractions({
  ghost,
  setGhost,
  commitGhost,
  clearGhost,
  draftDurations,
  setDraftDurations,
  totalDraftDuration,
  resizeSnapshotRef,
  resizeHandleRef,
  editingAppointment,
  workOrderId,
  appointmentId,
  technicians,
  breakBlocks,
  hasCollision,
  onDraftFinalized,
  dayIso,
}: UseTimelineInteractionsOptions) {
  const updatePosition = useDispositionDraftStore((s) => s.updatePosition)
  const isFullDay = useDispositionDraftStore((s) => s.isFullDay)
  const moveFullDayToTechnician = useDispositionDraftStore((s) => s.moveFullDayToTechnician)

  const getAppointmentsForTechnician = (technicianId: string) =>
    technicians?.find((t) => t.technicianId === technicianId)?.appointments ?? []

  const getBreakStartForTechnician = (technicianId: string) =>
    breakBlocks?.get(technicianId)?.startTimeIso

  const isWithinShift = (anfahrtStartIso: string, durations: DefaultDurations, technicianId: string) => {
    const tech = technicians?.find((t) => t.technicianId === technicianId)
    if (!tech) return true
    if (dayIso && !isTechWorkingDay(tech.workingDays, dayIso)) return false
    if (!tech.shiftStart || !tech.shiftEnd) return true
    const shiftStart = getHourFromIso(tech.shiftStart)
    const shiftEnd = getHourFromIso(tech.shiftEnd)
    const bd = new Date(anfahrtStartIso)
    const blockStart = bd.getHours() + bd.getMinutes() / 60
    const totalMin = durations.plannedTravelDuration + durations.plannedWorkDuration + durations.plannedDepartureDuration
    const blockEnd = blockStart + totalMin / 60
    return blockStart >= shiftStart && blockEnd <= shiftEnd
  }

  const snapDraft = (technicianId: string, rawIso: string) => {
    const appointments = getAppointmentsForTechnician(technicianId)
    return findNearestAvailableSlot(rawIso, totalDraftDuration, appointments, appointmentId, getBreakStartForTechnician(technicianId))
  }

  const getTechnicianName = (technicianId: string) =>
    technicians?.find((t) => t.technicianId === technicianId)?.technicianName ?? null

  const handleDraftStart = (technicianId: string, anfahrtStartIso: string) => {
    // When ghost is committed, allow moving to a different technician
    if (ghost?.committed) {
      if (ghost.technicianId === technicianId) return
      const tech = technicians?.find((t) => t.technicianId === technicianId)
      if (isFullDay) {
        moveFullDayToTechnician({
          technicianId: Number(technicianId),
          technicianName: tech?.technicianName ?? null,
          shiftStart: tech?.shiftStart ?? null,
          shiftEnd: tech?.shiftEnd ?? null,
        })
        onDraftFinalized?.(technicianId, tech?.technicianName ?? null, anfahrtStartIso, draftDurations, editingAppointment?.tradeCode)
        return
      }
      const appointments = getAppointmentsForTechnician(technicianId)
      const snapped = findNearestAvailableSlot(anfahrtStartIso, totalDraftDuration, appointments, appointmentId, getBreakStartForTechnician(technicianId))
      setGhost({ technicianId, anfahrtStartIso: snapped, committed: true })
      updatePosition(snapped)
      onDraftFinalized?.(technicianId, getTechnicianName(technicianId), snapped, draftDurations, editingAppointment?.tradeCode)
      return
    }

    const appointments = getAppointmentsForTechnician(technicianId)
    const snapped = findNearestAvailableSlot(anfahrtStartIso, totalDraftDuration, appointments, appointmentId, getBreakStartForTechnician(technicianId))
    setGhost({ technicianId, anfahrtStartIso: snapped })
  }

  const handleDraftMove = (technicianId: string, anfahrtStartIso: string) => {
    if (!ghost) return
    const snapped = snapDraft(technicianId, anfahrtStartIso)
    setGhost({ technicianId, anfahrtStartIso: snapped })
  }

  const handleDraftCommit = (technicianId: string, technicianName: string | null) => {
    if (!ghost || ghost.technicianId !== technicianId) return
    const { anfahrtStartIso } = ghost
    commitGhost()
    onDraftFinalized?.(technicianId, technicianName, anfahrtStartIso, draftDurations, editingAppointment?.tradeCode)
  }

  // ── Resize ──────────────────────────────────────────────────────────────

  const handleResizeStart = useCallback(
    (handle: ResizeHandle) => {
      if (!ghost) return
      resizeSnapshotRef.current = {
        durations: { ...draftDurations },
        anfahrtStartIso: ghost.anfahrtStartIso,
      }
      resizeHandleRef.current = handle
    },
    [draftDurations, ghost, resizeSnapshotRef, resizeHandleRef],
  )

  const handleResizeMove = useCallback(
    (minutesDelta: number) => {
      const snapshot = resizeSnapshotRef.current
      const handle = resizeHandleRef.current
      if (!snapshot || !handle || !ghost) return

      const newDurations = { ...snapshot.durations }
      let newAnfahrtIso = snapshot.anfahrtStartIso

      switch (handle) {
        case 'left': {
          const maxDelta = snapshot.durations.plannedTravelDuration
          const clampedDelta = Math.min(minutesDelta, maxDelta)
          const clampedStart = new Date(new Date(snapshot.anfahrtStartIso).getTime() + clampedDelta * 60_000)
          newDurations.plannedTravelDuration = Math.max(0, snapshot.durations.plannedTravelDuration - clampedDelta)
          newAnfahrtIso = clampedStart.toISOString()
          break
        }
        case 'travel-work': {
          const newTravel = Math.max(0, snapshot.durations.plannedTravelDuration + minutesDelta)
          const newWork = Math.max(15, snapshot.durations.plannedWorkDuration - minutesDelta)
          if (newTravel >= 0 && newWork >= 15) {
            newDurations.plannedTravelDuration = newTravel
            newDurations.plannedWorkDuration = newWork
          }
          break
        }
        case 'work-departure': {
          const newWork = Math.max(15, snapshot.durations.plannedWorkDuration + minutesDelta)
          newDurations.plannedWorkDuration = newWork
          break
        }
        case 'right': {
          const newDeparture = Math.max(0, snapshot.durations.plannedDepartureDuration + minutesDelta)
          newDurations.plannedDepartureDuration = newDeparture
          break
        }
      }

      if (hasCollision(newAnfahrtIso, newDurations, ghost.technicianId, appointmentId)) return
      if (!isWithinShift(newAnfahrtIso, newDurations, ghost.technicianId)) return

      if (newAnfahrtIso !== snapshot.anfahrtStartIso) {
        updatePosition(newAnfahrtIso)
      }

      setDraftDurations(newDurations)
    },
    [
      ghost,
      appointmentId,
      hasCollision,
      isWithinShift,
      setDraftDurations,
      updatePosition,
      resizeSnapshotRef,
      resizeHandleRef,
    ],
  )

  const handleResizeEnd = useCallback(() => {
    resizeSnapshotRef.current = null
    resizeHandleRef.current = null
  }, [resizeSnapshotRef, resizeHandleRef])

  // ── Block move ──────────────────────────────────────────────────────────

  const handleBlockMove = useCallback(
    (newAnfahrtStartIso: string) => {
      if (!ghost) return
      if (hasCollision(newAnfahrtStartIso, draftDurations, ghost.technicianId, appointmentId)) return
      if (!isWithinShift(newAnfahrtStartIso, draftDurations, ghost.technicianId)) return
      updatePosition(newAnfahrtStartIso)
    },
    [ghost, updatePosition, hasCollision, draftDurations, appointmentId, isWithinShift],
  )

  const handleBlockMoveEnd = useCallback(() => {
    // state already updated
  }, [])

  const handleBlockMoveToRow = useCallback(
    (newTechnicianId: string) => {
      if (!ghost) return
      if (ghost.technicianId === newTechnicianId) return
      const tech = technicians?.find((t) => t.technicianId === newTechnicianId)
      if (isFullDay) {
        moveFullDayToTechnician({
          technicianId: Number(newTechnicianId),
          technicianName: tech?.technicianName ?? null,
          shiftStart: tech?.shiftStart ?? null,
          shiftEnd: tech?.shiftEnd ?? null,
        })
        onDraftFinalized?.(newTechnicianId, tech?.technicianName ?? null, ghost.anfahrtStartIso, draftDurations, editingAppointment?.tradeCode)
        return
      }
      if (hasCollision(ghost.anfahrtStartIso, draftDurations, newTechnicianId, appointmentId)) return
      if (!isWithinShift(ghost.anfahrtStartIso, draftDurations, newTechnicianId)) return

      setGhost({ ...ghost, technicianId: newTechnicianId })
      onDraftFinalized?.(newTechnicianId, tech?.technicianName ?? null, ghost.anfahrtStartIso, draftDurations, editingAppointment?.tradeCode)
    },
    [ghost, draftDurations, setGhost, technicians, onDraftFinalized, editingAppointment, hasCollision, appointmentId, isWithinShift, isFullDay, moveFullDayToTechnician],
  )

  // ── Per-row factories ─────────────────────────────────────────────────

  const getDraftStart = (technicianId: string) => (iso: string) => {
    handleDraftStart(technicianId, iso)
  }

  const getDraftMove = (technicianId: string) => (iso: string) => {
    handleDraftMove(technicianId, iso)
  }

  const getDraftCommit = (technicianId: string, technicianName: string | null) => () => {
    handleDraftCommit(technicianId, technicianName)
  }

  const getDraftCancel = () => {
    clearGhost()
  }

  return {
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
    handleBlockMove,
    handleBlockMoveEnd,
    handleBlockMoveToRow,
    getDraftStart,
    getDraftMove,
    getDraftCommit,
    getDraftCancel,
  }
}
